<template lang="pug">
div
  template(v-if="!showEditMode")
    .d-flex.align-center(v-if="taxRate")
      .d-flex.align-center.flex-grow-1(style="gap: 6px;")
        .text-sm {{ taxRate.name }} ({{ taxRate.rate }}%)
        //- yz-btn(icon-only size="sm" icon="$yzi-edit-05" @click="openTaxableItemsDialog")
        v-btn(x-small icon @click="openTaxableItemsDialog")
          v-icon(color="neutral700" x-small) $yzi-edit-05
        v-btn(x-small icon @click="removeTaxRate" :loading="isDeleting")
          v-icon(small) mdi-close
      .text-sm.no-wrap(v-if="!hidePrice") {{ taxAmount | displayPrice }}
    .text-sm.font-semibold.neutral700--text.underline.clickable(v-else @click="showEditMode = true") {{ $t('add_item', { item: $t('tax').toLowerCase() }) }}
  template(v-else)
    .d-flex.align-center
      y-select.flex-grow-1(v-model="tax" :items="rates" item-value="id" item-text="name" noDataText="You have not set up any taxes yet" placeholder="Select tax" @change="selectTax" :loading="isLoading" small attach menu-height="180" :menu-position="menuPosition")
        template(#append-item)
          .px-4.py-3.text-sm.font-semibold.underline.clickable(v-if="!isHq" @click="onAddNewTax") {{ $t('record_new_tax_rate') }}
      v-btn.ml-2(small icon @click="showEditMode = false")
        v-icon(small) mdi-close

  yz-dialog(v-model="showTaxableItemsDialog" persistent scrollable width="700")
    yz-card
      yz-dialog-normal-header(title="Taxable Items" @close="closeTaxableItemsDialog")
      yz-dialog-normal-content
        div.mt-3
          .pa-6.text-center(v-if="invoiceItems.length === 0")
            .text-body-1.neutral500--text No items available
          v-data-table(v-else v-model="selectedTaxableItems" :headers="tableHeaders" :items="invoiceItems" item-key="id" show-select disable-sort hide-default-footer :items-per-page="-1" loader-height="2")
            template(v-slot:header.data-table-select="{ props }")
              yc-checkbox.ma-0(:input-value="allSelected" :indeterminate="props.indeterminate" :disabled="invoiceItems.length === 0" @change="handleSelectAll")
            template(v-slot:item.data-table-select="{ item, select, isSelected }")
              yc-checkbox.ma-0(:input-value="isSelected" @change="select($event)")
            template(v-slot:item.price="{ item }")
              span.no-wrap {{ item.price | displayPrice($currency) }}
            template(v-slot:item.total="{ item }")
              span.no-wrap {{ (item.price * item.quantity) | displayPrice($currency) }}
      yz-dialog-normal-actions(save-label="Save" cancel-label="Cancel" :loading="isLoadingTaxable" @save="saveTaxableItems" @cancel="closeTaxableItemsDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    rates: {
      type: Array,
      default: () => []
    },
    taxRate: {
      type: Object,
      default: null
    },
    taxAmount: {
      type: Number,
      default: 0
    },
    hidePrice: {
      type: Boolean
    },
    menuPosition: {
      type: String,
      default: 'bottom'
    },
    invoice: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      showEditMode: false,
      tax: null,
      isLoading: false,
      isLoadingTaxable: false,
      isDeleting: false,
      showTaxableItemsDialog: false,
      selectedTaxableItems: [],
      tableHeaders: [
        { text: 'Item Name', value: 'name', sortable: false },
        { text: 'Price', value: 'price', sortable: false },
        { text: 'Qty', value: 'quantity', sortable: false },
        { text: 'Total', value: 'total', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq'
    }),
    leadPid () {
      return this.$route.params.pid
    },
    invoiceItems () {
      return this.invoice.products || []
    },
    taxExcludeItems () {
      return (this.invoice.extra_data && this.invoice.extra_data.tax_exclude_items) || []
    },
    allItemIds () {
      return this.invoiceItems.map(item => item.id)
    },
    allSelected () {
      const ids = this.invoiceItems.map(item => item.id)
      return this.selectedTaxableItems.filter(item => ids.includes(item.id)).length === ids.length
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    removeTaxRate () {
      this.isDeleting = true
      const endpoint = `/invoices/${this.leadPid}`
      const payload = { tax_rate: null }
      this.axios.patch(endpoint, payload)
        .then(resp => {
          this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', null)
          this.createActivityLog('delete_tax', `Tax ${this.taxRate.name} (${this.taxRate.rate}%) was removed`)
          this.$emit('updated', resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    selectTax () {
      this.isLoading = true
      const endpoint = `/invoices/${this.leadPid}`
      const payload = { tax_rate: this.tax }
      this.axios.patch(endpoint, payload)
        .then(resp => {
          this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', null)
          const selectedRate = this.rates.find(r => r.id === this.tax)
          if (selectedRate) {
            this.createActivityLog('add_tax', `Tax ${selectedRate.name} (${selectedRate.rate}%) was added`)
          }
          this.showEditMode = false
          this.$emit('updated', resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onAddNewTax () {
      this.$router.push('/admin/clinic/settings/tax')
    },
    openTaxableItemsDialog () {
      // Initialize selectedTaxableItems: select full item objects NOT in taxExcludeItems
      this.selectedTaxableItems = this.invoiceItems.filter(
        item => !this.taxExcludeItems.includes(item.id)
      )
      this.showTaxableItemsDialog = true
    },
    closeTaxableItemsDialog () {
      this.showTaxableItemsDialog = false
      this.selectedTaxableItems = []
    },
    handleSelectAll (value) {
      if (value) {
        this.selectedTaxableItems = [...this.selectedTaxableItems, ...this.invoiceItems]
      } else {
        const itemIds = this.invoiceItems.map(item => item.id)
        this.selectedTaxableItems = this.selectedTaxableItems.filter(item => !itemIds.includes(item.id))
      }
    },
    saveTaxableItems () {
      // Convert selectedTaxableItems (full objects) back to taxExcludeItems (IDs)
      // Items NOT selected = excluded from tax
      const selectedItemIds = this.selectedTaxableItems.map(item => item.id)
      const newTaxExcludeItems = this.invoiceItems
        .filter(item => !selectedItemIds.includes(item.id))
        .map(item => item.id)

      this.isLoadingTaxable = true
      const endpoint = `/invoices/${this.leadPid}`
      const payload = { tax_exclude_items: newTaxExcludeItems }

      this.axios.patch(endpoint, payload)
        .then(resp => {
          this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', null)
          this.createActivityLog('edit_tax', `Taxable items updated - ${selectedItemIds.length} items included`)
          this.closeTaxableItemsDialog()
          this.$emit('updated', resp.data)
          this.showMessage(['success', 'Taxable items updated successfully'])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingTaxable = false
        })
    },
    updateOldActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'invoice',
        activity: `Update invoice (#${response.rid})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data).catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    }
  },
}
</script>

<style lang="scss" scoped>
</style>